import React, { useEffect, useState } from 'react';
import { Modal } from '@/components/molecules/Modal/Modal';
import { Button, Icon, Spinner } from '@/components/atoms';

// API response type from backend
interface AttachmentResponse {
  id: string;
  entity_type: string;
  entity_id: string;
  file_path: string;
  file_type: string;
  download_url: string;
  uploaded_by: string;
  uploaded_at: string;
  created_at: string;
  updated_at: string;
}

// Helper to extract filename from file_path
const getFilenameFromPath = (filePath: string): string => {
  const parts = filePath.split('/');
  return parts[parts.length - 1] || filePath;
};

export interface AttachmentViewerProps {
  attachment: AttachmentResponse;
  open: boolean;
  onClose: () => void;
  onNext?: () => void;
  onPrevious?: () => void;
  hasNext?: boolean;
  hasPrevious?: boolean;
}

export const AttachmentViewer: React.FC<AttachmentViewerProps> = ({
  attachment,
  open,
  onClose,
  onNext,
  onPrevious,
  hasNext = false,
  hasPrevious = false,
}) => {
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (open) {
      setIsLoading(true);
      setError(null);
    }
  }, [open, attachment.id]);

  const filename = getFilenameFromPath(attachment.file_path);

  const handleDownload = () => {
    // Create a temporary link and trigger download
    const link = document.createElement('a');
    link.href = attachment.download_url;
    link.download = filename;
    link.target = '_blank';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const renderPreview = () => {
    const fileType = attachment.file_type;

    // Image preview
    if (fileType.startsWith('image/')) {
      return (
        <div className="flex items-center justify-center bg-neutral-100 rounded-lg p-4">
          <img
            src={attachment.download_url}
            alt={filename}
            className="max-w-full max-h-[60vh] object-contain"
            onLoad={() => setIsLoading(false)}
            onError={() => {
              setIsLoading(false);
              setError('Failed to load image');
            }}
          />
        </div>
      );
    }

    // PDF preview
    if (fileType === 'application/pdf') {
      return (
        <div className="w-full h-[70vh] bg-neutral-100 rounded-lg overflow-hidden">
          <iframe
            src={attachment.download_url}
            className="w-full h-full"
            title={filename}
            onLoad={() => setIsLoading(false)}
            onError={() => {
              setIsLoading(false);
              setError('Failed to load PDF');
            }}
          />
        </div>
      );
    }

    // Text file preview
    if (fileType.startsWith('text/')) {
      return (
        <div className="bg-neutral-50 rounded-lg p-4 max-h-[60vh] overflow-auto">
          <pre className="text-sm font-mono whitespace-pre-wrap">
            <TextFilePreview url={attachment.download_url} onLoad={() => setIsLoading(false)} />
          </pre>
        </div>
      );
    }

    // Unsupported file type - show download option
    setIsLoading(false);
    return (
      <div className="text-center py-12">
        <div className="w-20 h-20 mx-auto bg-neutral-100 rounded-full flex items-center justify-center mb-4">
          <Icon name="File" size="xl" className="text-neutral-400" />
        </div>
        <p className="text-neutral-600 mb-2">Preview not available for this file type</p>
        <p className="text-sm text-neutral-500 mb-6">{attachment.file_type}</p>
        <Button variant="primary" onClick={handleDownload}>
          <Icon name="Download" size="sm" />
          <span className="ml-2">Download File</span>
        </Button>
      </div>
    );
  };

  const footer = (
    <div className="flex items-center justify-between">
      <div className="flex items-center gap-2">
        {hasPrevious && (
          <Button
            variant="secondary"
            onClick={onPrevious}
            disabled={!hasPrevious}
            aria-label="Previous attachment"
          >
            <Icon name="ChevronLeft" size="sm" />
            Previous
          </Button>
        )}
        {hasNext && (
          <Button
            variant="secondary"
            onClick={onNext}
            disabled={!hasNext}
            aria-label="Next attachment"
          >
            Next
            <Icon name="ChevronRight" size="sm" />
          </Button>
        )}
      </div>

      <Button variant="primary" onClick={handleDownload}>
        <Icon name="Download" size="sm" />
        <span className="ml-2">Download</span>
      </Button>
    </div>
  );

  return (
    <Modal
      isOpen={open}
      onClose={onClose}
      title={filename}
      size="xl"
      footer={hasPrevious || hasNext ? footer : undefined}
    >
      <div className="relative">
        {isLoading && (
          <div className="absolute inset-0 flex items-center justify-center bg-white bg-opacity-75 z-10">
            <Spinner size="lg" />
          </div>
        )}

        {error ? (
          <div className="bg-error-50 border border-error-200 rounded-lg p-4 text-center">
            <p className="text-error-700 mb-4">{error}</p>
            <Button variant="primary" onClick={handleDownload}>
              <Icon name="Download" size="sm" />
              <span className="ml-2">Download File</span>
            </Button>
          </div>
        ) : (
          <>
            <div className="mb-4 pb-4 border-b border-neutral-200">
              <div className="flex items-center justify-between text-sm text-neutral-600">
                <span>Type: {attachment.file_type}</span>
                <span>Uploaded: {new Date(attachment.uploaded_at).toLocaleDateString()}</span>
              </div>
            </div>
            {renderPreview()}
          </>
        )}
      </div>
    </Modal>
  );
};

/**
 * Component to fetch and display text file content
 */
const TextFilePreview: React.FC<{ url: string; onLoad: () => void }> = ({ url, onLoad }) => {
  const [content, setContent] = useState<string>('');
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    fetch(url)
      .then((response) => {
        if (!response.ok) {
          throw new Error('Failed to fetch file');
        }
        return response.text();
      })
      .then((text) => {
        setContent(text);
        onLoad();
      })
      .catch((err) => {
        setError(err instanceof Error ? err.message : 'Failed to load file');
        onLoad();
      });
  }, [url, onLoad]);

  if (error) {
    return <div className="text-error-600">{error}</div>;
  }

  return <>{content || 'Loading...'}</>;
};
